import { Midi } from '@tonaljs/tonal';
import NoSleep from 'nosleep.js';
import { clamp } from 'ramda';
import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { usePreview } from 'react-dnd-preview';
import { useSelector, useDispatch } from 'react-redux';

import { useAudio } from 'hooks';
import { appendBlock, deleteBlock, setBlockId, setTransport } from 'state/song.reducer';
import { tracksSelector, activeBlockIdSelector, tempoSelector, activeTrackIdSelector } from 'state/song.selectors';
import { Block, Track } from 'types';

import { TrackBlock } from './TrackBlock';

const noSleep = new NoSleep();

const ChordDragPlaceholder = () => {
  const {display, item, style} = usePreview();
  if (!display || item.type !== 'Chord') {
    return null;
  }
  return <div className="z-50 select-none from-teal-400 to-teal-600 text-gray-100 shadow-sm hover:shadow-md bg-gradient-to-br rounded-full h-12 w-12 inline-flex items-center justify-center m-1 flex-shrink-0"
    style={{ 
      fontSize: '0.625rem',
      ...style
    }}>{item.chord.symbol}</div>;
};

const BlockDragPlaceholder = () => {
  const {display, item, style } = usePreview();
  if (!display || item.type !== 'Block') {
    return null;
  }
  const { block } = item; 
  return (
    <div
      style={style}
      className="rounded-md text-black flex-none flex justify-around items-center cursor-pointer w-16 h-24 mx-1 flex-col"
    >
      { block?.chord?.symbol ? block?.chord?.symbol : 'empty' }
    </div>
  );
};

const Thrash = ({ enabled }: { enabled: boolean }) => {
  const activeBlockId = useSelector(activeBlockIdSelector);
  const activeTrackId = useSelector(activeTrackIdSelector);
  const dispatch = useDispatch();

  const remove = () => {
    dispatch(setBlockId()); //clear block
    if (enabled) {
      dispatch(deleteBlock({
        trackId: activeTrackId,
        id: activeBlockId || ''
      }));
    }
  };

  return (
    <button onClick={remove} disabled={!enabled}>
      <svg className={`h-8 w-8 ${enabled ? 'opacity-100' : 'opacity-25' } text-gray-200`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    </button>
  );
};

const DropArea = ({ track }: { track: any }) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [, dropRef] = useDrop({
    accept: 'Chord',
    drop: () => {
      //setTimeout(() => placeholderRef?.current?.scrollIntoView(), 0);
      return { track };
    }
  });
  const dispatch = useDispatch();

  const addNewBlock = (track: Track) => {
    dispatch(appendBlock(
      {
        trackId:  track.id,
        ...track.playground
      }
    ));
    //setTimeout(() => placeholderRef?.current?.scrollIntoView(), 0);
  };

  return (
    <div ref={placeholderRef} className="h-full">
      <div ref={dropRef} className="h-full">
        <div
          onClick={() => addNewBlock(track)}
          className="flex-none flex justify-center items-center cursor-pointer opacity-25 hover:opacity-75 border-2 border-dashed border-blue-800 shadow-md w-16 
      mx-1 rounded-md text-lg text-blue-900 flex-col h-full"
        >
          <span>+</span>
          <span className="font-bold text-center tracking-tighter leading-none" style={{ fontSize: '0.625rem'}}>Drop chord here!</span>
        </div>
      </div>
    </div>
  );
};

const TrackRow = ({ track }: { track: Track }) => {
  const { 
    arpeggiate, 
    skip, 
    isPlaying,
  } = useAudio();
  const activeBlockId = useSelector(activeBlockIdSelector);
  const dispatch = useDispatch();

  const remove = (block: Partial<Block>, track: Track) => {
    if (activeBlockId === block.id) {
      dispatch(setBlockId()); //clear block
    }
    dispatch(deleteBlock({ 
      trackId: track.id,
      id: block.id || ''
    }));
  };

  const select = (block: Partial<Block>, index: number) => {
    if (activeBlockId === block.id) {
      dispatch(setBlockId()); //clear block
    } else {
      dispatch(setBlockId(block.id));

      if (!isPlaying) {
        const notes = block.chord?.positions?.[block?.position || 0].midi.map(midiNote => Midi.midiToNoteName(midiNote)) || [];
        arpeggiate(notes);
      } else {
        skip(`${index}:0:0`);
      }
    }
  };

  return (
    <div key={track.id}>
      <div className="bg-teal-300 p-2 flex rounded-sm h-20">
        <div className="flex-none w-6 mx-2 flex justify-center items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="#000000" version="1.1" viewBox="0 0 100 100">
            <path d="M90.012,8.518L89.799,8.16c0.754-0.475,0.68-0.716,0.68-0.716s-0.371-1.008-1.445-0.368  c-1.07,0.645-0.359,1.449-0.359,1.449s0.18,0.177,0.943-0.256l0.197,0.336c-0.08,0.042-0.977,0.521-2.271,1.21l-0.193-0.33  c0.752-0.474,0.68-0.716,0.68-0.716s-0.373-1.007-1.449-0.368c-1.07,0.646-0.357,1.449-0.357,1.449s0.176,0.179,0.941-0.255  l0.193,0.321c-0.656,0.35-1.4,0.745-2.186,1.163l-0.223-0.371c0.754-0.475,0.682-0.717,0.682-0.717s-0.373-1.007-1.447-0.368  c-1.07,0.645-0.359,1.449-0.359,1.449S84,11.25,84.766,10.817l0.217,0.364c-0.719,0.382-1.465,0.78-2.203,1.174l-0.19-0.321  c0.752-0.476,0.681-0.717,0.681-0.717s-0.373-1.008-1.447-0.369c-1.071,0.646-0.36,1.449-0.36,1.449s0.181,0.179,0.942-0.255  l0.188,0.313c-0.771,0.412-1.525,0.814-2.229,1.188l-0.184-0.309c0.756-0.474,0.682-0.717,0.682-0.717s-0.373-1.007-1.446-0.367  c-1.069,0.645-0.36,1.449-0.36,1.449s0.178,0.177,0.944-0.256l0.178,0.3c-0.889,0.473-1.668,0.891-2.25,1.203l-0.163-0.276  c0.752-0.474,0.68-0.716,0.68-0.716s-0.37-1.008-1.446-0.368c-1.072,0.646-0.36,1.449-0.36,1.449s0.178,0.179,0.944-0.256  l0.158,0.27c-0.328,0.176-0.58,0.313-0.728,0.393c-1.453,0.796-1.835,1.106-1.558,1.833c0.276,0.726,0.006,1.458,0.004,1.459  c-0.019,0.017-0.039,0.038-0.067,0.065c-0.412,0.398-1.847,1.791-2.601,2.522c-1.916,1.858-3.83,3.713-5.744,5.57  c-2.426,2.354-4.854,4.708-7.28,7.063c-2.481,2.406-4.964,4.812-7.442,7.219c-2.711,2.632-5.445,5.242-8.146,7.884  c-0.792,0.777-1.509,1.578-2.731,1.3c-1.091-0.248-1.995-1.252-2.408-2.262c-0.864-2.111-0.087-4.4,1.277-6.1  c0.654-0.814,2.151-2.124,1.152-3.208c-1.601-1.735-4.646,0.403-5.986,1.482c-3.238,2.61-4.645,6.029-6.514,9.59  c-0.941,1.793-2.085,3.495-3.719,4.729c-1.622,1.227-3.445,1.68-5.325,2.33c-1.157,0.4-2.299,0.843-3.415,1.35  c-4.167,1.894-8.674,4.944-10.038,9.568c-0.666,2.254-0.365,4.602,0.518,6.752c0.452,1.102,1.004,2.158,1.633,3.169  c2.504,4.021,6.03,7.591,9.594,10.687c1.744,1.517,3.716,2.902,5.998,3.434c2.748,0.641,5.819,0.075,8.257-1.304  c4.857-2.748,7.449-11.632,7.449-11.632c0.331-1.062,0.659-2.125,1.084-3.15c0.761-1.835,1.89-3.381,3.572-4.471  c0.83-0.535,1.724-0.965,2.616-1.385c1.091-0.513,2.185-0.951,3.218-1.578c1.552-0.941,2.993-2.17,3.803-3.828  c0.233-0.48,0.535-1.122,0.554-1.68c0.024-0.802-0.315-1.828-1.226-1.978c-0.925-0.151-1.677,0.542-2.418,0.978  c-0.938,0.551-1.959,1.017-3.05,1.148c-0.927,0.113-1.769-0.264-2.491-0.821c-0.949-0.733-2.045-2.18-2.075-3.456  c-0.002-0.117-0.037-0.197,0.058-0.296c0.031-0.032,31.75-32.822,34.772-35.844c3.025-3.025,5.549-1.503,5.549-1.503  c1.695-1.659,3.891-5.41,4.787-6.793c0.9-1.382,2.18,0.242,3.732-0.587c1.557-0.83,2.594-3.111,1.16-4.685  C92.367,7.702,90.578,8.284,90.012,8.518z M39.562,52.711c0.634,2.104,1.523,4.114,2.426,6.113c0.977,2.164,1.793,4.693,4.342,5.422  c0.582,0.165,1.287,0.279,1.893,0.234c1.5-0.107,2.794-0.832,4.039-1.621c0.273-0.174,0.856-0.709,1.129-0.312  c0.272,0.395-0.067,1.227-0.246,1.584c-0.412,0.834-1.125,1.508-1.836,2.09c-2.394,1.959-5.368,2.723-7.918,4.389  c-1.292,0.845-2.149,2.062-2.807,3.438c-1.059,2.212-1.905,4.525-2.801,6.808c-0.133,0.34-0.267,0.681-0.402,1.021  c-0.433,1.097-0.893,2.146-1.466,3.179c-0.479,0.861-1.409,2.461-2.663,2.189c-0.46-0.102-0.777-0.562-0.967-0.962  c-0.458-0.964-0.432-2.134-0.441-3.175c-0.011-1.269-0.348-2.601-0.88-3.748c-0.471-1.016-1.133-1.897-1.878-2.717  c-0.734-0.809-1.549-1.554-2.335-2.301c-1.191-1.132-2.299-2.355-3.537-3.438c-0.816-0.719-1.987-1.611-2.306-2.711  c-0.793-2.731,1.656-5.295,3.748-6.607c0.668-0.42,1.386-0.771,2.111-1.086c2.567-1.109,5.087-2.314,7.129-4.273  c1.232-1.184,2.043-2.559,2.991-3.955c0.268-0.393,0.568-0.847,1.031-1.021C38.951,50.85,39.327,51.936,39.562,52.711z"/><circle cx="33.902" cy="84.067" r="1.434"/><circle cx="33.463" cy="79.112" r="1.435"/><circle cx="32.081" cy="74.273" r="1.434"/><path d="M35.811,65.986c0.406,0.404,0.406,1.062,0,1.469l0,0c-0.406,0.404-1.064,0.404-1.47,0l-4.077-4.078  c-0.406-0.406-0.406-1.064,0-1.47l0,0c0.405-0.406,1.063-0.406,1.469,0L35.811,65.986z"/><path d="M31.011,70.988c0.324,0.474,0.203,1.121-0.271,1.443l0,0c-0.473,0.322-1.121,0.2-1.444-0.271L26.04,67.4  c-0.324-0.476-0.203-1.121,0.271-1.443l0,0c0.474-0.325,1.121-0.203,1.445,0.271L31.011,70.988z"/><path d="M40.615,61.182c0.406,0.404,0.406,1.062,0,1.469l0,0c-0.405,0.404-1.063,0.406-1.469,0l-4.078-4.076  c-0.406-0.406-0.406-1.064,0-1.471l0,0c0.406-0.405,1.063-0.405,1.469,0L40.615,61.182z"/></svg>
        </div>
        <div className="flex hidden-scrollbar overflow-auto overflow-scrolling">
          {track.blocks.map((block, i) => (
            <TrackBlock
              key={`block-${i}`}
              block={block} 
              isActive={block.id === activeBlockId}
              onClick={(evt: MouseEvent) => select(block, i)} 
              onRemove={() => remove(block, track)}
            />
          ))}
          <DropArea track={track} />
        </div>
      </div>
    </div>
  );
};

export const Composer: FunctionComponent = () => {
  const { 
    togglePlay,  
    isPlaying,
    canPlay,
    metronome: { 
      toggle: toggleMetronome, 
      mute: isMetronomeMuted 
    }
  } = useAudio();
  const playRef = useRef<SVGSVGElement>();
  const tracks = useSelector(tracksSelector) as Track[];
  const tempo = useSelector(tempoSelector);
  const activeBlockId = useSelector(activeBlockIdSelector);
  const dispatch = useDispatch();

  const setTempo = (tempo: number) => {
    dispatch(setTransport({ tempo }));
  };

  const enableNoSleep = useCallback(() => {
    playRef.current?.removeEventListener('click', enableNoSleep, false);
    noSleep.enable();
  }, []);

  const playRefCallback = (node: SVGSVGElement) => {
    if (node) {
      // Enable wake lock.
      // (must be wrapped in a user input event handler e.g. a mouse or touch handler)
      playRef.current = node;
      playRef.current.addEventListener('click', enableNoSleep, false);
    }
  };

  useEffect(() => () => playRef?.current?.removeEventListener('click', enableNoSleep), [enableNoSleep]);

  return (
    <div>
      <BlockDragPlaceholder />
      <ChordDragPlaceholder />
      {/* Tracks */}
      {tracks.map(track => <TrackRow key={track.id} track={track} />)}

      <div className="flex items-center justify-between p-2 bg-gray-500">
        <div className="flex items-start">
          <span onClick={toggleMetronome}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="#000000" version="1.1" viewBox="0 0 256 256" className={`h-8 w-8 inline cursor-pointer fill-current text-gray-700 ${isMetronomeMuted ? 'text-opacity-50' : 'text-opacity-100'}`}>
              <path d="M136.5,34.2l6.2,8.2c1.1,1.4-1.5,3.3-3.3,3.3h-23c-1.8,0-4.4-1.9-3.3-3.3l6.2-8.2C123.4,29,132.6,29,136.5,34.2L136.5,34.2z  "/>
              <g>
                <path d="M174.4,212.5H95.5c-4.6,0-9.2-2.1-12.2-5.6c-2.5-3-3.6-6.6-3-10.3l21.4-137.6c1.4-8.8,7.6-14.7,15.3-14.7h35.9   c7.6,0,13.9,6.1,15.2,14.7l21.5,137.6c0.6,3.6-0.5,7.3-3,10.3C183.6,210.4,179,212.5,174.4,212.5z M117,52.4c-4.2,0-6.7,4-7.3,7.9   L88.2,197.9c-0.3,1.7,0.5,3,1.2,3.8c1.4,1.7,3.8,2.8,6.1,2.8h78.9c2.2,0,4.6-1.1,6.1-2.8c0.7-0.8,1.5-2.2,1.2-3.8L160.2,60.3   c-0.6-3.9-3.1-7.9-7.3-7.9H117z"/>
                <path d="M81.3,212.1c-2,0-3.6-1.6-3.6-3.6s1.7-3.6,3.6-3.6V212.1z M81.3,204.9h14.8v7.3H81.3V204.9z M96.1,204.9   c2,0,3.6,1.6,3.6,3.6s-1.7,3.6-3.6,3.6V204.9z"/>
                <path d="M70,212.1c-2,0-3.7-1.6-3.7-3.6c0-2,1.7-3.5,3.7-3.5V212.1z M70,205h0.6v7.1H70V205z M70.5,205c2,0,3.6,1.5,3.6,3.5   c0,2-1.7,3.6-3.6,3.6V205z"/><path d="M133.8,168.8c-1.7,0-3.2-1.1-3.8-2.7L90.2,48.8c-0.7-2.1,0.4-4.4,2.5-5.1c2.1-0.7,4.4,0.4,5.1,2.5l39.8,117.3   c0.7,2.1-0.4,4.4-2.5,5.1C134.7,168.7,134.2,168.8,133.8,168.8z"/>
                <path d="M111.6,99.2L111.6,99.2c4-1.4,8.4,0.8,9.8,4.8l2.3,6.9c1.4,4-0.8,8.4-4.8,9.8l0,0c-4,1.4-8.5-0.8-9.8-4.8l-2.3-6.9   C105.4,104.9,107.6,100.6,111.6,99.2L111.6,99.2z"/><path d="M180.4,172h-91c-2.2,0-4-1.8-4-4s1.8-4,4-4h91c2.2,0,4,1.8,4,4S182.7,172,180.4,172z"/><path d="M158.3,191h-46.7c-2.2,0-4-1.8-4-4s1.8-4,4-4h46.7c2.2,0,4,1.8,4,4S160.5,191,158.3,191z"/><path d="M138.9,48.4h14c6.2,0,10.3,5.2,11.2,11.3l21.5,137.6c1,6.1-5.1,11.2-11.2,11.2h-14c6.2,0,12.2-5.1,11.3-11.2L150.2,59.7   C149.3,53.6,145.2,48.4,138.9,48.4L138.9,48.4z"/>
              </g>
            </svg>
          </span>
          <input 
            type="number" 
            value={tempo} 
            onChange={evt => setTempo(clamp(1, 300, parseInt(evt.target.value) || 0))}
            className="w-16 bg-transparent text-2xl text-gray-700 font-semibold leading-none inline-block" />
          {/* <span className="text-md ml-2 font-light text-gray-700">4/4</span> */}
        </div>

        <div className="-ml-16" onClick={() => canPlay && togglePlay()}>
          { isPlaying ? (
            <svg className="h-8 w-8 text-gray-200 cursor-pointer"  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) :
            (
              <svg ref={playRefCallback} className="h-8 w-8 text-gray-200 cursor-pointer" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            )}
        </div>

        <Thrash enabled={!!activeBlockId} />
      </div>
    </div>
  );
};
